import { useEffect, useState } from "react";
import { CalendarPlus, Check, Mail, Phone, MapPin } from "lucide-react";
import { Slot } from "../../types/booking";
import { formatSlotDateLong, formatSlotTime } from "../../lib/timezone";
import { downloadIcs, describeWhen } from "../../lib/ics";
import { getFooter } from "../../services/api";
import { SummaryPackage } from "./booking-summary";

interface BookingConfirmationProps {
  confirmation: { slot: Slot; name: string };
  pkg: SummaryPackage | null;
  glamSpot?: "studio" | "home";
  homeAddress?: string;
  onHome: () => void;
}

interface Contact {
  email?: string;
  phone_number?: string;
  address?: string;
}

const STUDIO_FALLBACK = "Charmé Studio, Halifax, Nova Scotia";

export function BookingConfirmation({
  confirmation,
  pkg,
  glamSpot,
  homeAddress,
  onHome,
}: BookingConfirmationProps) {
  const [contact, setContact] = useState<Contact | null>(null);

  useEffect(() => {
    let active = true;
    getFooter()
      .then((res: any) => {
        if (active) setContact(res?.data?.data?.[0] ?? null);
      })
      .catch(() => {
        /* contact block simply hides if unavailable */
      });
    return () => {
      active = false;
    };
  }, []);

  const { slot, name } = confirmation;
  const where =
    glamSpot === "home"
      ? homeAddress || "Your home"
      : contact?.address || STUDIO_FALLBACK;

  const handleAddToCalendar = () => {
    downloadIcs(
      {
        start: slot.start,
        end: slot.end,
        title: pkg ? `${pkg.title} — Charmé` : "Charmé glam session",
        description: `Your glam session with Charmé.\n${describeWhen(
          slot.start,
          slot.end
        )}`,
        location: where,
      },
      "charme-booking.ics"
    );
  };

  return (
    <div className="min-h-screen bg-[#FBEFEF] px-4 py-12 md:py-20">
      <div className="animate-charme-rise mx-auto max-w-xl">
        {/* The resolving moment */}
        <div className="flex flex-col items-center text-center">
          <div
            className="animate-charme-pop flex h-20 w-20 items-center justify-center rounded-full bg-[#B87775]/15"
            style={{ animationDelay: "120ms" }}
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#B87775]">
              <Check className="h-7 w-7 text-white" aria-hidden="true" />
            </span>
          </div>
          <p className="mt-6 text-xs uppercase tracking-[0.3em] text-[#B87775]">
            Booking confirmed
          </p>
          <h1 className="mt-3 font-serif text-4xl leading-tight text-[#3a2e2c]">
            You're all booked{name ? `, ${name.split(" ")[0]}` : ""}
          </h1>
          <p className="mt-3 max-w-md text-[#7c6c6a]">
            A confirmation is on its way to your inbox. We can't wait to glam
            you.
          </p>
        </div>

        {/* Receipt */}
        <div className="mt-10 overflow-hidden rounded-2xl border border-[#EAD7D6] bg-white">
          <div className="border-b border-[#EAD7D6] bg-[#FBEFEF]/70 px-6 py-4">
            <p className="text-xs uppercase tracking-[0.2em] text-[#B87775]">
              Your appointment
            </p>
          </div>
          <dl className="divide-y divide-[#F0E2E1] px-6">
            {pkg && (
              <div className="flex items-center justify-between py-4">
                <dt className="text-sm uppercase tracking-[0.14em] text-[#9A6B69]/80">
                  Package
                </dt>
                <dd className="font-serif text-lg text-[#3a2e2c]">{pkg.title}</dd>
              </div>
            )}
            <div className="flex items-center justify-between py-4">
              <dt className="text-sm uppercase tracking-[0.14em] text-[#9A6B69]/80">
                Date
              </dt>
              <dd className="text-right font-serif text-lg text-[#3a2e2c]">
                {formatSlotDateLong(slot.start)}
              </dd>
            </div>
            <div className="flex items-center justify-between py-4">
              <dt className="text-sm uppercase tracking-[0.14em] text-[#9A6B69]/80">
                Time
              </dt>
              <dd className="text-right font-serif text-lg tabular-nums text-[#3a2e2c]">
                {formatSlotTime(slot.start)} – {formatSlotTime(slot.end)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-6 py-4">
              <dt className="text-sm uppercase tracking-[0.14em] text-[#9A6B69]/80">
                Where
              </dt>
              <dd className="text-right text-[#3a2e2c]">{where}</dd>
            </div>
          </dl>

          <div className="px-6 pb-6">
            <button
              type="button"
              onClick={handleAddToCalendar}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#B87775] px-5 py-3 text-sm font-medium text-[#B87775] transition hover:bg-[#FBEFEF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
            >
              <CalendarPlus className="h-4 w-4" aria-hidden="true" />
              Add to calendar
            </button>
          </div>
        </div>

        {/* What happens next */}
        <div className="mt-8 rounded-2xl border border-[#EAD7D6] bg-white p-6">
          <h2 className="font-serif text-xl text-[#3a2e2c]">What happens next</h2>
          <ol className="mt-4 space-y-3 text-[15px] text-[#7c6c6a]">
            {[
              "Look out for your confirmation email with all the details.",
              "We'll review your notes and prepare for your session.",
              glamSpot === "home"
                ? "We'll arrive at your address at the booked time."
                : "Arrive a few minutes early so we can start relaxed.",
            ].map((line, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FBEFEF] text-xs tabular-nums text-[#B87775]">
                  {i + 1}
                </span>
                <span>{line}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Contact */}
        {(contact?.email || contact?.phone_number) && (
          <div className="mt-8 text-center">
            <p className="text-sm text-[#9A6B69]">
              Need to change something? Reach us any time.
            </p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
              {contact?.email && (
                <a
                  href={`mailto:${contact.email}`}
                  className="inline-flex items-center gap-2 text-[#B87775] hover:text-[#a96764]"
                >
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  {contact.email}
                </a>
              )}
              {contact?.phone_number && (
                <a
                  href={`tel:${contact.phone_number}`}
                  className="inline-flex items-center gap-2 text-[#B87775] hover:text-[#a96764]"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  {contact.phone_number}
                </a>
              )}
            </div>
            {glamSpot !== "home" && where && (
              <p className="mt-3 inline-flex items-center gap-2 text-sm text-[#9A6B69]">
                <MapPin className="h-4 w-4" aria-hidden="true" />
                {where}
              </p>
            )}
          </div>
        )}

        <div className="mt-10 text-center">
          <button
            type="button"
            onClick={onHome}
            className="rounded-full bg-[#B87775] px-8 py-3 text-sm font-medium text-white transition hover:bg-[#a96764] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
          >
            Back to homepage
          </button>
        </div>
      </div>
    </div>
  );
}
